/**
 * Bounded retry with jitter. Section 8 allows at most 30 seconds of trying and
 * forbids unbounded loops, so the budget is a property of the object rather
 * than a discipline the caller has to remember.
 */
export class RetryBudget {
  readonly #totalMs: number;
  readonly #baseMs: number;
  readonly #maxMs: number;
  #startedAt: number | null = null;
  #attempt = 0;

  constructor({ totalMs = 30_000, baseMs = 500, maxMs = 5_000 } = {}) {
    this.#totalMs = totalMs;
    this.#baseMs = baseMs;
    this.#maxMs = maxMs;
  }

  reset(): void {
    this.#startedAt = null;
    this.#attempt = 0;
  }

  get attempt(): number {
    return this.#attempt;
  }

  /** Milliseconds to wait, or null once the budget is spent. */
  next(now = Date.now()): number | null {
    this.#startedAt ??= now;
    if (now - this.#startedAt > this.#totalMs) return null;

    const exponential = Math.min(this.#baseMs * 2 ** this.#attempt, this.#maxMs);
    this.#attempt += 1;
    // Full jitter. Two browsers dropped by the same blip must not come back in
    // lockstep and collide again.
    return Math.round(exponential * (0.5 + Math.random() * 0.5));
  }
}

export const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
